import React, { useState, useEffect } from 'react';
import CookieConsentLib from 'react-cookie-consent';
import Cookies from 'js-cookie';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import './CookieConsent.css';
import logger from '../../services/Logger';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    functional: true,
    marketing: false
  });

  useEffect(() => {
    const savedPreferences = Cookies.get('cookie-preferences');
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (error) {
        logger.error('Failed to parse cookie preferences:', error);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    savePreferences(allAccepted);
    initializeServices(allAccepted);
  };

  const handleAcceptSelected = () => {
    savePreferences(preferences);
    initializeServices(preferences);
    setShowDetails(false);
  };

  const handleDeclineOptional = () => {
    const minimalPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false
    };
    savePreferences(minimalPreferences);
    initializeServices(minimalPreferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    Cookies.set('cookie-preferences', JSON.stringify(prefs), { 
      expires: 365,
      sameSite: 'strict',
      secure: true 
    });
    Cookies.set('gdpr-consent', 'true', { 
      expires: 365,
      sameSite: 'strict',
      secure: true 
    });
    logger.info('Cookie preferences saved:', prefs);
  };

  const initializeServices = (prefs: CookiePreferences) => {
    // Initialize Google Analytics
    if (prefs.analytics && process.env.REACT_APP_GA_TRACKING_ID) {
      // Initialize GA4
      logger.info('Google Analytics initialized');
    }

    // Initialize functional features
    if (prefs.functional) {
      // Enable enhanced features
      logger.info('Functional cookies enabled');
    }

    // Initialize marketing tools
    if (prefs.marketing) {
      // Initialize marketing pixels
      logger.info('Marketing cookies enabled');
    }
  };

  const detailsFooter = (
    <div className="cookie-consent-footer">
      <Button 
        label="Rifiuta opzionali" 
        className="p-button-text" 
        onClick={handleDeclineOptional}
      />
      <Button 
        label="Accetta selezionati" 
        className="p-button-success" 
        onClick={handleAcceptSelected}
      />
    </div>
  );

  return (
    <>
      <CookieConsentLib
        location="bottom"
        buttonText="Accetta tutti"
        declineButtonText="Solo necessari"
        cookieName="gdpr-consent"
        style={{
          background: '#2B373B',
          fontSize: '14px',
          padding: '20px',
          alignItems: 'center'
        }}
        buttonStyle={{
          background: 'var(--primary-color, #229a61)',
          color: '#ffffff',
          fontSize: '14px',
          padding: '10px 20px',
          borderRadius: '4px',
          fontWeight: 'bold'
        }}
        declineButtonStyle={{
          background: 'transparent',
          color: '#ffffff',
          fontSize: '14px',
          padding: '10px 20px',
          border: '1px solid #ffffff',
          borderRadius: '4px'
        }}
        expires={365}
        enableDeclineButton
        onAccept={handleAcceptAll}
        onDecline={handleDeclineOptional}
      >
        <div className="cookie-consent-content">
          <h4>Informativa sui Cookie</h4>
          <p>
            Questo sito utilizza cookie tecnici e, previo tuo consenso, cookie di profilazione 
            per migliorare la tua esperienza di navigazione e per analisi statistiche.
          </p>
          <Button
            label="Personalizza"
            className="p-button-link p-button-sm"
            onClick={(e) => {
              e.preventDefault();
              setShowDetails(true);
            }}
            style={{ color: '#ffffff', textDecoration: 'underline' }}
          />
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#ffffff', marginLeft: '10px' }}
          >
            Privacy Policy
          </a>
        </div>
      </CookieConsentLib>

      <Dialog
        header="Gestione Cookie"
        visible={showDetails}
        style={{ width: '600px' }}
        footer={detailsFooter}
        onHide={() => setShowDetails(false)}
        modal
      >
        <div className="cookie-preferences">
          <div className="cookie-category">
            <h5>Cookie Necessari</h5>
            <p>Essenziali per il funzionamento del sito. Non possono essere disabilitati.</p>
            <input 
              type="checkbox" 
              checked={preferences.necessary} 
              disabled 
            />
          </div>

          <div className="cookie-category">
            <h5>Cookie Analitici</h5>
            <p>Ci aiutano a capire come i visitatori interagiscono con il sito.</p>
            <input 
              type="checkbox" 
              checked={preferences.analytics}
              onChange={(e) => setPreferences({
                ...preferences,
                analytics: e.target.checked
              })}
            />
          </div>

          <div className="cookie-category">
            <h5>Cookie Funzionali</h5>
            <p>Abilitano funzionalità avanzate e personalizzazione.</p>
            <input 
              type="checkbox" 
              checked={preferences.functional}
              onChange={(e) => setPreferences({
                ...preferences,
                functional: e.target.checked
              })}
            />
          </div>

          <div className="cookie-category">
            <h5>Cookie di Marketing</h5>
            <p>Utilizzati per mostrare annunci pertinenti ai tuoi interessi.</p>
            <input 
              type="checkbox" 
              checked={preferences.marketing}
              onChange={(e) => setPreferences({
                ...preferences,
                marketing: e.target.checked
              })}
            />
          </div>

          <div className="cookie-info">
            <p>
              <strong>Nota GDPR:</strong> In conformità con il Regolamento Generale sulla 
              Protezione dei Dati (GDPR), hai il diritto di accettare o rifiutare i cookie 
              non essenziali. I tuoi dati personali saranno trattati secondo la nostra 
              Privacy Policy.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CookieConsent;